import { Alert, AlertDescription } from '@/components/ui/alert.js';
import { CustomDialog } from '@/components/ui/custom-dialog.js';
import { pluralize } from '@/lib/string-utils.js';
import { Lightbulb } from 'lucide-react';

import { CoachAssignedProps } from '../$schoolId/students.js';

interface DialogCoachesAssignedProps extends CoachAssignedProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function DialogCoachesAssigned({
  availableCoachesCount,
  coachesAssignedCount,
  reservedFallbackGroupCount = 0,
  open,
  setOpen,
}: DialogCoachesAssignedProps) {
  if (availableCoachesCount < 0 || coachesAssignedCount < 0) {
    return null;
  }

  const assignmentTexts = (count: number) => {
    if (count <= 0) {
      return {
        title: 'No teachers were assigned',
        description: "Check the teachers' information and availability before assigning them manually.",
      };
    }

    const [plural, verb] = count === 1 ? ['', 'was'] : ['s', 'were'];

    return {
      title: `Teacher${plural} assigned`,
      description: `${count} available teacher${plural} ${verb} assigned automatically.`,
    };
  };

  const { title, description } = assignmentTexts(coachesAssignedCount);
  const groupsLabel = pluralize(reservedFallbackGroupCount, 'group', 'groups');

  return (
    <CustomDialog title={title} text={description} open={open} setOpen={setOpen} closeButtonText="Close">
      {reservedFallbackGroupCount > 0 && (
        <Alert
          variant="alert"
          className="border-orange-300 bg-orange-100 px-3 py-2 text-orange-700 [&>svg]:top-2.5 [&>svg]:text-orange-700"
          data-testid="teacher-assigned-banner-reserved-fallback"
        >
          <Lightbulb className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="font-semibold text-orange-700">
            Reserved teachers assigned to {reservedFallbackGroupCount} {groupsLabel} due to no eligible non-reserved
            teacher could be assigned.
          </AlertDescription>
        </Alert>
      )}
    </CustomDialog>
  );
}
